import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"
import styled from "styled-components"

import { useLazyGetFulfilledOrdersNewQuery, useLazyGetOrdersNewQuery } from "store/api/ordersApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showEditOrderModal, showNewOrderModal } from "store/modalSlise"
import { setCurrentOrderNew } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import { TableActionButtonGroup } from "types/table/tableActionButton"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"
import { ReactComponent as IconEdit } from "assets/svg/icon-edit.svg"
import { ReactComponent as StatusArchivedIcon } from "assets/svg/status-archived.svg"

import { convertOrdersNewToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useExportCSV from "hooks/useExportCSV"
import { useOrderTypeOptions } from "hooks/useOrderTypeOptions"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { DeleteOrderModalNew } from "modals/DeleteOrderModalNew"
import { OrderFulfillmentModal } from "modals/OrderFulfillmentModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { ButtonNew } from "components/form/ButtonNew"
import { CheckboxNew } from "components/form/CheckboxNew"
import TableWrapper from "components/table/TableWrapper"

import { getOrderTableHeaderCells } from "./getOrderTableHeaderCells"

import ordersStyles from "./index.module.scss"

const StyledStatusArchived = styled(StatusArchivedIcon)`
  path {
    stroke: #64748b;
  }

  &:hover {
    path {
      stroke: #334155;
    }
  }
`

export const Orders = () => {
  const dispatch = useAppDispatch()
  const showV2 = useAppSelector(isFeatureEnabled(FeatureFlag.ThermV2Ordering))
  const showSensors = useAppSelector(isFeatureEnabled(FeatureFlag.SensorsOrdering))
  const [triggerGetOrders, { isFetching: isOrdersFetching, data: ordersData }] = useLazyGetOrdersNewQuery()
  const [triggerGetFulfilledOrders, { isFetching: isFulfilledFetching, data: fulfilledData }] =
    useLazyGetFulfilledOrdersNewQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [isFulfillChecked, checkFulfill] = useState(false)
  const [isFirstRender, setFirstRender] = useState(true)
  const [toBeFulfilledOrder, setToBeFulfilledOrder] = useState<ApiTypes.Model.OrderNew | null>(null)
  const [toBeDeletedOrder, setToBeDeletedOrder] = useState<number | null>(null)

  const relevantData = isFulfillChecked ? fulfilledData : ordersData
  const relevantFetching = isFulfillChecked ? isFulfilledFetching : isOrdersFetching
  const orderTypeOptions = useOrderTypeOptions()

  const filteredTableHeaderCells = useMemo(
    () => getOrderTableHeaderCells(isFulfillChecked, showV2, showSensors, orderTypeOptions),
    [isFulfillChecked, showV2, showSensors, orderTypeOptions],
  )

  const convertData = useMemo(
    () => (data?: ApiTypes.Model.OrderNew[]) => convertOrdersNewToTableRows(data, showV2),
    [showV2],
  )

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: filteredTableHeaderCells,
    meta: relevantData?.meta,
    fileName: "orders",
    convertData,
    useLazyQuery: isFulfillChecked ? useLazyGetFulfilledOrdersNewQuery : useLazyGetOrdersNewQuery,
  })

  const onSetCurrentOrderNew = (id: number) => {
    const ordersList = relevantData?.data
    const currentOrder = ordersList?.find(item => item.id === id)
    if (!currentOrder) {
      return
    }
    dispatch(setCurrentOrderNew(currentOrder))
  }

  const onEditClick = (id: number) => {
    onSetCurrentOrderNew(id)
    dispatch(showEditOrderModal(true))
  }

  const onDeleteClick = (id: number) => {
    setToBeDeletedOrder(id)
  }

  const onFulfillClickRow = (id: number) => {
    const ordersList = relevantData?.data
    const currentOrder = ordersList?.filter(item => !!item.items.some(item => item.id === id))[0]

    if (!currentOrder) {
      return
    }

    setToBeFulfilledOrder({ ...currentOrder, items: currentOrder.items.filter(item => item.id === id) })
  }

  const onNewClick = () => {
    dispatch(showNewOrderModal(true))
  }

  const getRowActions = (rowData: UITypes.TableBodyRow): TableActionButtonGroup => {
    const item = rowData.rowSettings.item
    const status = rowData.item_status.value
    const id = rowData.rowSettings.id

    const isMainRow = !!rowData.subRows
    const items = Array.isArray(item) ? item : [item]
    const firstItem = items[0]
    const isShippingStatus = firstItem?.status === "shipping"

    return {
      className: classNames(ordersStyles.ordersActionsWrapper, {
        [ordersStyles.singleItem]: rowData.subRows && rowData.subRows.length === 0,
      }),
      actionButtons: [
        {
          icon: <IconDelete />,
          tooltip: "Delete",
          tooltipPosition: "top" as const,
          hidden: !(isMainRow && status === "new"),
          handler: () => onDeleteClick(id),
          className: ordersStyles.deleteAction,
        },
        {
          icon: <IconEdit />,
          tooltip: "Edit",
          tooltipPosition: "top" as const,
          hidden: !(isMainRow && status === "new"),
          handler: () => onEditClick(id),
          className: ordersStyles.editAction,
        },
        {
          icon: <StyledStatusArchived />,
          tooltip: "Fulfill",
          tooltipPosition: "top" as const,
          hidden: (isMainRow && items.length !== 1) || !isShippingStatus,
          handler: () => onFulfillClickRow(items[0].id),
        },
      ],
    }
  }

  const getOrders = async (params: UITypes.Params) => {
    return isFulfillChecked ? await triggerGetFulfilledOrders(params) : await triggerGetOrders(params)
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await getOrders(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    sendRequest(urlQueryParams)
  }, [urlQueryParams, isFirstRender, isFulfillChecked])

  const handleCheckFulfill = (event: React.ChangeEvent<HTMLInputElement>) => {
    checkFulfill(event?.currentTarget?.checked)
    if (event?.currentTarget?.checked) {
      setUrlQueryParams({
        item_status: null,
      })
    } else {
      setUrlQueryParams({
        fulfillment_date_start: null,
        fulfillment_date_end: null,
      })
    }
  }

  return (
    <>
      <ContentWrapper title="Orders history">
        <nav className={styles.tableNavWrapper}>
          <div>
            <ButtonNew onClick={onNewClick} color="blue">
              New order
            </ButtonNew>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <CheckboxNew
              className={styles.tableNavItem}
              isReverse={true}
              id="fulfilled"
              isChecked={isFulfillChecked}
              onChange={handleCheckFulfill}
            >
              Show fulfilled
            </CheckboxNew>
            <ButtonNew
              onClick={handleExport}
              className={styles.tableNavItem}
              disabled={relevantData?.meta?.total === 0}
            >
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          tableName="contractor-orders"
          headerCells={filteredTableHeaderCells}
          data={convertData(relevantData?.data)}
          isLoading={relevantFetching}
          getRowActions={getRowActions}
          clearAdditionalTags={() => dispatch(setCurrentOrderNew(undefined))}
          rowsExpanded
        />
        <Pagination meta={relevantData?.meta} />
      </ContentWrapper>
      {toBeFulfilledOrder && (
        <OrderFulfillmentModal
          order={toBeFulfilledOrder}
          onCancel={() => setToBeFulfilledOrder(null)}
          onSuccess={() => setToBeFulfilledOrder(null)}
        />
      )}
      {toBeDeletedOrder && (
        <DeleteOrderModalNew orderId={toBeDeletedOrder} onCancel={() => setToBeDeletedOrder(null)} />
      )}
    </>
  )
}
